import { useLocalObservable } from 'mobx-react-lite';

import { debounce } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { MIN_SEARCH_LENGTH } from 'APP/constants/search';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { User } from 'STORE/Users/User/User';

import { SEARCH_DEBOUNCE, OFFSET_STEP } from '../SelectContacts.constants';
import { IUseExtendedUsersPresenterProps, IExtendedUsersPresenter } from '../SelectContacts.types';

export const useExtendedUsersPresenter = ({
  groupId,
}: IUseExtendedUsersPresenterProps): IExtendedUsersPresenter => {
  const presenter = useLocalObservable<IExtendedUsersPresenter>(() => ({
    offset: 0,
    hasMore: false,
    usersIds: [],
    isLoading: false,
    searchValue: '',
    cursor: null,

    get group(): Group | null {
      return groupId ? Entities.GroupsStore.getGroupById(groupId) : null;
    },

    get users(): User[] {
      return presenter.usersIds
        .map((id) => Entities.usersStore.getUserById(id))
        .filter((user): user is User => Boolean(user));
    },

    get isShowExtendedUsers(): boolean {
      return presenter.isLoading || presenter.users.length > 0;
    },

    onSearchValueChange(value: string): void {
      presenter.resetSearch();
      presenter.searchValue = value;

      if (value.length < MIN_SEARCH_LENGTH) {
        return;
      }

      presenter.isLoading = true;
      presenter.loadUsers(value);
    },

    resetSearch(): void {
      presenter.offset = 0;
      presenter.hasMore = false;
      presenter.usersIds = [];
      presenter.isLoading = false;
      presenter.cursor = null;
    },

    isNeedSearch(searchValue: string): boolean {
      return presenter.searchValue === searchValue;
    },

    loadUsers: debounce(async (searchValue: string): Promise<void> => {
      // ignore if the search value was changed during debounce execution
      if (!presenter.isNeedSearch(searchValue)) {
        return;
      }

      const { users, hasMore, cursor } = await Tasks.search.searchAddUsersToGroup({
        query: searchValue,
        offset: '0',
        groupId: presenter.group?.id,
        cursor: presenter.cursor || undefined,
      });

      const userIds = users.map((user) => user.id);
      // ignore if the search value was changed during the task execution
      if (!presenter.isNeedSearch(searchValue)) {
        return;
      }

      presenter.cursor = cursor || null;
      presenter.usersIds = userIds;
      presenter.hasMore = hasMore;
      presenter.isLoading = false;
    }, SEARCH_DEBOUNCE),

    async loadMore(): Promise<void> {
      if (!presenter.hasMore || presenter.isLoading) {
        return;
      }

      const offset = presenter.offset + OFFSET_STEP;
      const searchValue = presenter.searchValue;
      presenter.isLoading = true;

      const { users, hasMore, cursor } = await Tasks.search.searchAddUsersToGroup({
        query: searchValue,
        offset: offset.toString(),
        groupId: presenter.group?.id,
        cursor: presenter.cursor || undefined,
      });

      const userIds = users.map((user) => user.id);
      // ignore if the search value was changed during the task execution
      if (!presenter.isNeedSearch(searchValue)) {
        return;
      }

      presenter.usersIds = [...presenter.usersIds, ...userIds];
      presenter.offset = offset;
      presenter.hasMore = hasMore;
      presenter.isLoading = false;
      presenter.cursor = cursor || null;
    },
  }));

  return presenter;
};
